import { datadogRum } from '@datadog/browser-rum'
import {
  DATADOG_APP_KEY,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RECORDING_BUSINESSES_ALLOWED,
  ENV_NAME,
  VERSION_COMMIT_HASH,
} from 'config/envVariables'
import { IUser } from 'models/User'
import { IBusinessEntity } from 'models/BusinessEntity'

/**
 * Business IDs for which session recording should be enabled.
 */
const SESSION_RECORDING_ENABLED_BUSINESSES: number[] = DATADOG_RECORDING_BUSINESSES_ALLOWED
  ? DATADOG_RECORDING_BUSINESSES_ALLOWED.split(',')
      .map((id: string) => parseInt(id.trim(), 10))
      .filter((id: number) => !isNaN(id))
  : []

export default class MonitoringService {
  private static currentBusinessId: number | null = null

  /**
   * Initialization of the monitoring services.
   * Initializes DataDog RUM with session replay configuration.
   * Session recording must be started separately using startSessionRecordingForBusiness().
   */
  static initialization = () => {
    if (datadogRum.getInitConfiguration()) {
      return // Already initialized, skip. Otherwise, we get a console error
    }
    datadogRum.init({
      applicationId: DATADOG_APP_KEY as string,
      clientToken: DATADOG_CLIENT_TOKEN as string,
      service: 'spectre',
      env: ENV_NAME,
      allowedTracingUrls: [
        'https://api.staging.upshift.work',
        'https://api.upshift.work',
        'https://api.int.upshift.work',
        'https://api.dev.upshift.work',
      ],
      version: VERSION_COMMIT_HASH,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: true,
      traceSampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enableExperimentalFeatures: ['clickmap'],
    })
  }

  /**
   * Checks if session recording should be enabled for a given business.
   * @param businessEntity The business entity to check
   * @returns True if session recording should be enabled
   */
  private static shouldRecordSessionForBusiness = (
    businessEntity: IBusinessEntity,
  ): boolean => {
    return SESSION_RECORDING_ENABLED_BUSINESSES.includes(businessEntity.id)
  }

  /**
   * Starts session recording if the business is in the allowed list.
   * Stops recording if switching to a different business.
   * @param businessEntity The logged-in user's business entity
   */
  static startSessionRecordingForBusiness = (
    businessEntity: IBusinessEntity,
  ) => {
    const isInWhitelist = this.shouldRecordSessionForBusiness(businessEntity)
    const businessChanged = this.currentBusinessId !== businessEntity.id

    if (businessChanged) {
      // Always stop recording when business changes
      this.stopSessionRecording()
      this.currentBusinessId = businessEntity.id

      if (isInWhitelist) {
        datadogRum.startSessionReplayRecording()
      }
    } else if (isInWhitelist) {
      datadogRum.startSessionReplayRecording()
    }
  }

  /**
   * Stops session recording.
   * Safe to call even if not recording - DataDog handles it.
   */
  static stopSessionRecording = () => {
    datadogRum.stopSessionReplayRecording()
  }

  /**
   * Logs errors to monitoring services.
   * @param error Error object
   */
  static logError = (error: any) => {
    datadogRum.addError(error)
  }

  /**
   * Sets the logged in user in monitoring services.
   * @param user Currently logged in user
   */
  static setUser = (user: IUser) => {
    datadogRum.setUser({
      email: user.email,
      id: user.id.toString(),
      name: user.full_name,
    })
    datadogRum.setUserProperty('role', user.role)
  }

  /**
   * Sets the business entity ID as a user property in DataDog.
   * Also automatically starts session recording if business ID is in the whitelist.
   * @param businessEntity The business entity to set
   */
  static setBusinessEntity = (businessEntity: IBusinessEntity) => {
    datadogRum.setUserProperty('business_id', businessEntity.id.toString())
    this.startSessionRecordingForBusiness(businessEntity)
  }

  /**
   * Removes the current logged in user from monitoring services.
   * Ends the current session to ensure a fresh session on next login.
   */
  static clearUser = () => {
    this.stopSessionRecording()
    this.currentBusinessId = null

    // End current session so next login starts fresh
    try {
      datadogRum.stopSession()
    } catch {
      try {
        sessionStorage.removeItem('_dd_s')
        localStorage.removeItem('_dd_s')
      } catch {}
    }
  }
}
